import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, CheckCircle, Loader2, Calendar, User, Mail, Globe, Briefcase, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactModal = ({ isOpen, onClose }) => {
  const { t, currentLang } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [calLink, setCalLink] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    investor_type: '',
    country: '',
    investment_range: '',
    interests: [],
    message: '',
    consent: false,
  });

  // Multi-language content for the stepper
  const stepperContent = {
    en: {
      step1Title: "Let's get to know you",
      step1Subtitle: "Basic information to personalize your experience",
      step2Title: "Your investment profile",
      step2Subtitle: "Help us understand your preferences",
      step3Title: "You're all set",
      step3Subtitle: "Review and submit your inquiry",
      nextStep: "Next Step",
      prevStep: "Back",
      submit: "Submit & Continue",
      submitting: "Submitting...",
      successTitle: "Thank You",
      successMessage: "Your inquiry has been received successfully.",
      confirmationMessage: "Our team will review your information and contact you within 24-48 hours to schedule a personalized introduction call.",
      closeButton: "Close",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      investorType: "What best describes you?",
      country: "Country / Region",
      investmentRange: "Investment Range",
      interests: "Areas of Interest",
      message: "Anything else you'd like us to know? (Optional)",
      consent: "I consent to be contacted regarding this inquiry",
      reviewTitle: "Review Your Information",
      step: "Step"
    },
    it: {
      step1Title: "Conosciamoci",
      step1Subtitle: "Informazioni di base per personalizzare la tua esperienza",
      step2Title: "Il tuo profilo di investimento",
      step2Subtitle: "Aiutaci a capire le tue preferenze",
      step3Title: "Tutto pronto",
      step3Subtitle: "Rivedi e invia la tua richiesta",
      nextStep: "Prossimo",
      prevStep: "Indietro",
      submit: "Invia e Continua",
      submitting: "Invio in corso...",
      successTitle: "Grazie",
      successMessage: "La tua richiesta è stata ricevuta con successo.",
      confirmationMessage: "Il nostro team esaminerà le tue informazioni e ti contatterà entro 24-48 ore per organizzare una chiamata introduttiva personalizzata.",
      closeButton: "Chiudi",
      firstName: "Nome",
      lastName: "Cognome",
      email: "Indirizzo Email",
      investorType: "Come ti descriveresti?",
      country: "Paese / Regione",
      investmentRange: "Range di Investimento",
      interests: "Aree di Interesse",
      message: "C'è altro che vorresti farci sapere? (Opzionale)",
      consent: "Acconsento ad essere contattato riguardo questa richiesta",
      reviewTitle: "Rivedi le Tue Informazioni",
      step: "Passo"
    },
    'es-es': {
      step1Title: "Conozcámonos",
      step1Subtitle: "Información básica para personalizar tu experiencia",
      step2Title: "Tu perfil de inversión",
      step2Subtitle: "Ayúdanos a entender tus preferencias",
      step3Title: "Todo listo",
      step3Subtitle: "Revisa y envía tu consulta",
      nextStep: "Siguiente",
      prevStep: "Atrás",
      submit: "Enviar y Continuar",
      submitting: "Enviando...",
      successTitle: "Gracias",
      successMessage: "Tu consulta ha sido recibida con éxito.",
      confirmationMessage: "Nuestro equipo revisará tu información y te contactará en 24-48 horas para programar una llamada introductoria personalizada.",
      closeButton: "Cerrar",
      firstName: "Nombre",
      lastName: "Apellido",
      email: "Correo Electrónico",
      investorType: "¿Qué te describe mejor?",
      country: "País / Región",
      investmentRange: "Rango de Inversión",
      interests: "Áreas de Interés",
      message: "¿Algo más que quieras que sepamos? (Opcional)",
      consent: "Consiento ser contactado respecto a esta consulta",
      reviewTitle: "Revisa Tu Información",
      step: "Paso"
    },
    'es-latam': {
      step1Title: "Conozcámonos",
      step1Subtitle: "Información básica para personalizar tu experiencia",
      step2Title: "Tu perfil de inversión",
      step2Subtitle: "Ayúdanos a entender tus preferencias",
      step3Title: "Todo listo",
      step3Subtitle: "Revisa y envía tu consulta",
      nextStep: "Siguiente",
      prevStep: "Atrás",
      submit: "Enviar y Continuar",
      submitting: "Enviando...",
      successTitle: "Gracias",
      successMessage: "Tu consulta ha sido recibida con éxito.",
      confirmationMessage: "Nuestro equipo revisará tu información y te contactará en 24-48 horas para agendar una llamada introductoria personalizada.",
      closeButton: "Cerrar",
      firstName: "Nombre",
      lastName: "Apellido",
      email: "Correo Electrónico",
      investorType: "¿Qué te describe mejor?",
      country: "País / Región",
      investmentRange: "Rango de Inversión",
      interests: "Áreas de Interés",
      message: "¿Algo más que quieras que sepamos? (Opcional)",
      consent: "Consiento ser contactado respecto a esta consulta",
      reviewTitle: "Revisa Tu Información",
      step: "Paso"
    }
  };

  const content = stepperContent[currentLang] || stepperContent.en;

  // Complete list of countries in alphabetical order
  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
    'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
    'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
    'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
    'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait',
    'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
    'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru',
    'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'São Tomé and Príncipe',
    'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
    'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
    'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  // Funnel Analytics Tracking
  const trackFunnelEvent = async (eventType, eventData = {}) => {
    try {
      await axios.post(`${API_URL}/api/analytics/funnel`, {
        event_type: eventType,
        event_data: {
          ...eventData,
          timestamp: new Date().toISOString(),
          language: currentLang,
        }
      });
    } catch (error) {
      console.log('Analytics tracking failed:', error);
    }
  };

  // Translated investor types
  const getInvestorTypeLabel = (value) => {
    const labels = {
      angel: { en: 'Angel Investor', it: 'Angel Investor', es: 'Inversor Ángel' },
      family_office: { en: 'Family Office', it: 'Family Office', es: 'Family Office' },
      vc: { en: 'Venture Capital', it: 'Venture Capital', es: 'Venture Capital' },
      pe: { en: 'Private Equity', it: 'Private Equity', es: 'Private Equity' },
      institutional: { en: 'Institutional Investor', it: 'Investitore Istituzionale', es: 'Inversor Institucional' },
      corporate: { en: 'Corporate / Strategic', it: 'Corporate / Strategico', es: 'Corporativo / Estratégico' },
      hnwi: { en: 'High Net Worth Individual', it: 'High Net Worth Individual', es: 'Individuo de Alto Patrimonio' },
      other: { en: 'Other', it: 'Altro', es: 'Otro' }
    };
    const lang = currentLang?.startsWith('es') ? 'es' : (currentLang === 'it' ? 'it' : 'en');
    return labels[value]?.[lang] || labels[value]?.en || value;
  };

  const investorTypes = [
    { value: 'angel', label: getInvestorTypeLabel('angel') },
    { value: 'family_office', label: getInvestorTypeLabel('family_office') },
    { value: 'vc', label: getInvestorTypeLabel('vc') },
    { value: 'pe', label: getInvestorTypeLabel('pe') },
    { value: 'institutional', label: getInvestorTypeLabel('institutional') },
    { value: 'corporate', label: getInvestorTypeLabel('corporate') },
    { value: 'hnwi', label: getInvestorTypeLabel('hnwi') },
    { value: 'other', label: getInvestorTypeLabel('other') }
  ];

  // Placeholders translations
  const getPlaceholder = (key) => {
    const placeholders = {
      selectType: { en: 'Select type', it: 'Seleziona tipo', es: 'Seleccionar tipo' },
      selectCountry: { en: 'Select country', it: 'Seleziona paese', es: 'Seleccionar país' },
      selectRange: { en: 'Select range', it: 'Seleziona range', es: 'Seleccionar rango' }
    };
    const lang = currentLang?.startsWith('es') ? 'es' : (currentLang === 'it' ? 'it' : 'en');
    return placeholders[key]?.[lang] || placeholders[key]?.en || key;
  };

  const investmentRanges = [
    '€25K – €50K',
    '€50K – €75K', 
    '€75K – €100K',
    '€100K+'
  ];

  const interestOptions = [
    { value: 'capital_allocation', label: 'Capital Allocation' },
    { value: 'revenue_growth', label: 'Revenue Growth' },
    { value: 'ibv', label: 'Institutional Brand Valuation' },
    { value: 'active_execution', label: 'Active Execution' },
    { value: 'structured_decision', label: 'Structured Decision-Making' },
    { value: 'networking', label: 'High-Level Networking' }
  ];

  // Grouped interest options for organized display
  const getInterestLabel = (key) => {
    const labels = {
      investment_strategy: { en: 'Investment Strategy', it: 'Strategia di Investimento', es: 'Estrategia de Inversión' },
      execution_growth: { en: 'Execution & Growth', it: 'Esecuzione & Crescita', es: 'Ejecución & Crecimiento' },
      decision_access: { en: 'Decision & Access', it: 'Decisione & Accesso', es: 'Decisión & Acceso' },
      capital_allocation: { en: 'Capital Allocation', it: 'Allocazione del Capitale', es: 'Asignación de Capital' },
      ibv: { en: 'Institutional Brand Valuation', it: 'Valutazione Istituzionale del Brand', es: 'Valuación Institucional de Marca' },
      active_execution: { en: 'Active Execution', it: 'Esecuzione Attiva', es: 'Ejecución Activa' },
      revenue_growth: { en: 'Revenue Growth', it: 'Crescita dei Ricavi', es: 'Crecimiento de Ingresos' },
      structured_decision: { en: 'Structured Decision-Making', it: 'Decision-Making Strutturato', es: 'Toma de Decisiones Estructurada' },
      networking: { en: 'High-Level Networking', it: 'High-Level Networking', es: 'Networking de Alto Nivel' }
    };
    const lang = currentLang?.startsWith('es') ? 'es' : (currentLang === 'it' ? 'it' : 'en');
    return labels[key]?.[lang] || labels[key]?.en || key;
  };

  const interestGroups = [
    {
      label: getInterestLabel('investment_strategy'),
      options: [
        { value: 'capital_allocation', label: getInterestLabel('capital_allocation') },
        { value: 'ibv', label: getInterestLabel('ibv') }
      ]
    },
    {
      label: getInterestLabel('execution_growth'),
      options: [
        { value: 'active_execution', label: getInterestLabel('active_execution') },
        { value: 'revenue_growth', label: getInterestLabel('revenue_growth') }
      ]
    },
    {
      label: getInterestLabel('decision_access'),
      options: [
        { value: 'structured_decision', label: getInterestLabel('structured_decision') },
        { value: 'networking', label: getInterestLabel('networking') }
      ]
    }
  ];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/config`);
        if (response.data.cal_link) {
          setCalLink(response.data.cal_link);
        }
      } catch (error) {
        console.log('Config not available');
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setTimeout(() => {
        setCurrentStep(1);
        setSubmitSuccess(false);
        setSubmitError('');
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          investor_type: '',
          country: '',
          investment_range: '',
          interests: [],
          message: '',
          consent: false,
        });
      }, 300);
    }
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitError('');
  };

  const handleInterestToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(value)
        ? prev.interests.filter(i => i !== value)
        : [...prev.interests, value]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.first_name && formData.last_name && formData.email && formData.investor_type;
      case 2:
        return formData.country && formData.investment_range;
      case 3:
        return formData.consent;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Track step completion
      trackFunnelEvent(`step_${currentStep}_completed`, {
        step: currentStep,
        investor_type: currentStep === 1 ? formData.investor_type : undefined,
        country: currentStep === 2 ? formData.country : undefined,
        investment_range: currentStep === 2 ? formData.investment_range : undefined,
      });
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await axios.post(`${API_URL}/api/leads/investor`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        investor_type: formData.investor_type,
        country: formData.country,
        language: currentLang || 'en',
        ticket_size: formData.investment_range,
        investment_range: formData.investment_range,
        investment_focus: formData.interests.length > 0 ? formData.interests.join(', ') : 'General',
        message: formData.message || '',
        consent: formData.consent,
      });
      setSubmitSuccess(true);
      // Track form submission
      trackFunnelEvent('form_submitted', {
        investor_type: formData.investor_type,
        country: formData.country,
        investment_range: formData.investment_range,
        interests: formData.interests,
      });
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors which come as array
      if (Array.isArray(errorDetail)) {
        setSubmitError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setSubmitError(errorDetail || 'An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookCall = () => {
    // Track calendar booking click
    trackFunnelEvent('calendar_booked', {
      cal_link: calLink || 'adilsonsequeira/20-min-consultation',
    });
    // Use calLink from API or fallback to default
    const linkToUse = calLink || 'adilsonsequeira/20-min-consultation';
    window.open(`https://cal.com/${linkToUse}`, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  // Progress indicator
  const ProgressIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              currentStep >= step 
                ? 'bg-[#1A3A4A] text-white' 
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {currentStep > step ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              step
            )}
          </div>
          {step < 3 && (
            <div className={`w-12 h-0.5 mx-1 ${currentStep > step ? 'bg-[#1A3A4A]' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="contact-modal"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* Success State */}
          {submitSuccess ? (
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-2">
                  {content.successTitle}
                </h3>
                <p className="font-body text-[#1A3A4A]/70 mb-4">
                  {content.successMessage}
                </p>
              </div>

              {/* Book a Call Section */}
              <div className="bg-[#F8FAFB] rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#60B5AF]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-[#60B5AF]" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-[#1A3A4A]">
                      {currentLang?.startsWith('es') ? '¿Quieres agendar una llamada?' : currentLang === 'it' ? 'Vuoi prenotare una chiamata?' : 'Want to book a call?'}
                    </h4>
                    <p className="font-body text-sm text-[#1A3A4A]/60">
                      {currentLang?.startsWith('es') ? 'Selecciona la duración que prefieras' : currentLang === 'it' ? 'Seleziona la durata che preferisci' : 'Select your preferred duration'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      trackFunnelEvent('calendar_booked', { duration: '15min' });
                      window.open('https://cal.com/adilson-sequeira-5wlnel/15min', '_blank');
                    }}
                    className="p-4 bg-white border border-[#1A3A4A]/10 rounded-xl hover:border-[#60B5AF] hover:bg-[#60B5AF]/5 transition-all group"
                    data-testid="book-15min-btn"
                  >
                    <div className="font-heading text-2xl font-bold text-[#1A3A4A] group-hover:text-[#60B5AF]">15</div>
                    <div className="font-body text-sm text-[#1A3A4A]/60">
                      {currentLang?.startsWith('es') ? 'minutos' : currentLang === 'it' ? 'minuti' : 'minutes'}
                    </div>
                    <div className="font-body text-xs text-[#1A3A4A]/40 mt-1">
                      {currentLang?.startsWith('es') ? 'Intro rápida' : currentLang === 'it' ? 'Intro veloce' : 'Quick intro'}
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      trackFunnelEvent('calendar_booked', { duration: '30min' });
                      window.open('https://cal.com/adilson-sequeira-5wlnel/30min', '_blank');
                    }}
                    className="p-4 bg-white border border-[#1A3A4A]/10 rounded-xl hover:border-[#60B5AF] hover:bg-[#60B5AF]/5 transition-all group"
                    data-testid="book-30min-btn"
                  >
                    <div className="font-heading text-2xl font-bold text-[#1A3A4A] group-hover:text-[#60B5AF]">30</div>
                    <div className="font-body text-sm text-[#1A3A4A]/60">
                      {currentLang?.startsWith('es') ? 'minutos' : currentLang === 'it' ? 'minuti' : 'minutes'}
                    </div>
                    <div className="font-body text-xs text-[#1A3A4A]/40 mt-1">
                      {currentLang?.startsWith('es') ? 'Consulta completa' : currentLang === 'it' ? 'Consulenza completa' : 'Full consultation'}
                    </div>
                  </button>
                </div>
              </div>

              {/* Confirmation Message */}
              <div className="bg-[#1A3A4A]/5 rounded-xl p-4 mb-6">
                <p className="font-body text-sm text-[#1A3A4A]/70 leading-relaxed">
                  {content.confirmationMessage}
                </p>
              </div>

              {/* Close Button */}
              <Button
                data-testid="close-success-btn"
                onClick={onClose}
                className="w-full bg-[#1A3A4A] text-white hover:bg-[#1A3A4A]/90 font-body font-medium rounded-full py-6"
              >
                {content.closeButton}
              </Button>
            </div>
          ) : (
            <div className="p-6">
              {/* Progress Indicator */}
              <ProgressIndicator />

              {/* Step Content */}
              <AnimatePresence mode="wait">
                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="text-center mb-6">
                      <h2 className="font-heading text-xl font-semibold text-[#1A3A4A] mb-1">
                        {content.step1Title}
                      </h2>
                      <p className="font-body text-sm text-[#1A3A4A]/60">
                        {content.step1Subtitle}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                            {content.firstName} *
                          </Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A3A4A]/30" />
                            <Input
                              value={formData.first_name}
                              onChange={(e) => handleChange('first_name', e.target.value)}
                              className="pl-10 h-11 bg-[#F8FAFB] border-0 rounded-xl"
                              placeholder="John"
                              data-testid="input-first-name"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                            {content.lastName} *
                          </Label>
                          <Input
                            value={formData.last_name}
                            onChange={(e) => handleChange('last_name', e.target.value)}
                            className="h-11 bg-[#F8FAFB] border-0 rounded-xl"
                            placeholder="Smith"
                            data-testid="input-last-name"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                          {content.email} *
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A3A4A]/30" />
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="pl-10 h-11 bg-[#F8FAFB] border-0 rounded-xl"
                            placeholder="john@example.com"
                            data-testid="input-email"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                          {content.investorType} *
                        </Label>
                        <Select 
                          value={formData.investor_type} 
                          onValueChange={(v) => handleChange('investor_type', v)}
                        >
                          <SelectTrigger className="h-11 bg-[#F8FAFB] border-0 rounded-xl" data-testid="select-investor-type">
                            <Briefcase className="w-4 h-4 text-[#1A3A4A]/30 mr-2" />
                            <SelectValue placeholder={getPlaceholder('selectType')} />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {investorTypes.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Investment Profile */}
                {currentStep === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="text-center mb-6">
                      <h2 className="font-heading text-xl font-semibold text-[#1A3A4A] mb-1">
                        {content.step2Title}
                      </h2>
                      <p className="font-body text-sm text-[#1A3A4A]/60">
                        {content.step2Subtitle}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                          {content.country} *
                        </Label>
                        <Select 
                          value={formData.country} 
                          onValueChange={(v) => handleChange('country', v)}
                        >
                          <SelectTrigger className="h-11 bg-[#F8FAFB] border-0 rounded-xl" data-testid="select-country">
                            <Globe className="w-4 h-4 text-[#1A3A4A]/30 mr-2" />
                            <SelectValue placeholder={getPlaceholder('selectCountry')} />
                          </SelectTrigger>
                          <SelectContent className="bg-white max-h-60">
                            {countries.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                          {content.investmentRange} *
                        </Label>
                        <Select 
                          value={formData.investment_range} 
                          onValueChange={(v) => handleChange('investment_range', v)}
                        >
                          <SelectTrigger className="h-11 bg-[#F8FAFB] border-0 rounded-xl" data-testid="select-investment-range">
                            <SelectValue placeholder={getPlaceholder('selectRange')} />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {investmentRanges.map(range => (
                              <SelectItem key={range} value={range}>{range}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="font-body text-sm text-[#1A3A4A]/80 mb-3 block">
                          {content.interests}
                        </Label>
                        <div className="space-y-4">
                          {interestGroups.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              {/* Group Label */}
                              <p className="font-body text-xs text-[#1A3A4A]/50 uppercase tracking-wider mb-2">
                                {group.label}
                              </p>
                              {/* Group Options */}
                              <div className="grid grid-cols-2 gap-2">
                                {group.options.map(option => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleInterestToggle(option.value)}
                                    className={`p-3 rounded-xl text-left text-sm font-body transition-all ${
                                      formData.interests.includes(option.value)
                                        ? 'bg-[#1A3A4A] text-white'
                                        : 'bg-[#F8FAFB] text-[#1A3A4A]/70 hover:bg-[#1A3A4A]/5'
                                    }`}
                                    data-testid={`interest-${option.value}`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Review & Confirm */}
                {currentStep === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="text-center mb-6">
                      <h2 className="font-heading text-xl font-semibold text-[#1A3A4A] mb-1">
                        {content.step3Title}
                      </h2>
                      <p className="font-body text-sm text-[#1A3A4A]/60">
                        {content.step3Subtitle}
                      </p>
                    </div>

                    {/* Summary */}
                    <div className="bg-[#F8FAFB] rounded-xl p-4 mb-4 space-y-3 overflow-hidden">
                      <h4 className="font-body text-sm font-medium text-[#1A3A4A]">
                        {content.reviewTitle}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className="truncate">
                          <span className="text-[#1A3A4A]/50">Name:</span>
                          <span className="ml-2 text-[#1A3A4A]">{formData.first_name} {formData.last_name}</span>
                        </div>
                        <div className="truncate" title={formData.email}>
                          <span className="text-[#1A3A4A]/50">Email:</span>
                          <span className="ml-2 text-[#1A3A4A] break-all">{formData.email}</span>
                        </div>
                        <div className="truncate">
                          <span className="text-[#1A3A4A]/50">Type:</span>
                          <span className="ml-2 text-[#1A3A4A]">
                            {investorTypes.find(t => t.value === formData.investor_type)?.label}
                          </span>
                        </div>
                        <div className="truncate">
                          <span className="text-[#1A3A4A]/50">Country:</span>
                          <span className="ml-2 text-[#1A3A4A]">{formData.country}</span>
                        </div>
                        <div className="col-span-1 sm:col-span-2 truncate">
                          <span className="text-[#1A3A4A]/50">Investment:</span>
                          <span className="ml-2 text-[#1A3A4A]">{formData.investment_range}</span>
                        </div>
                      </div>
                    </div>

                    {/* Optional Message */}
                    <div className="mb-4">
                      <Label className="font-body text-sm text-[#1A3A4A]/80 mb-1.5 block">
                        {content.message}
                      </Label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        className="w-full h-20 p-3 bg-[#F8FAFB] border-0 rounded-xl font-body text-sm resize-none focus:ring-2 focus:ring-[#60B5AF] focus:outline-none"
                        placeholder="..."
                        data-testid="input-message"
                      />
                    </div>

                    {/* Consent */}
                    <div className="flex items-start gap-3 mb-4">
                      <Checkbox
                        id="consent"
                        checked={formData.consent}
                        onCheckedChange={(checked) => handleChange('consent', checked)}
                        data-testid="checkbox-consent"
                      />
                      <Label 
                        htmlFor="consent" 
                        className="font-body text-sm text-[#1A3A4A]/70 cursor-pointer leading-relaxed"
                      >
                        {content.consent} *
                      </Label>
                    </div>

                    {submitError && (
                      <div className="p-3 bg-red-50 text-red-600 rounded-xl font-body text-sm mb-4">
                        {submitError}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex gap-3 mt-6">
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    className="flex-1 border-[#1A3A4A]/20 text-[#1A3A4A] hover:bg-[#1A3A4A]/5 rounded-full h-12"
                    data-testid="btn-back"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {content.prevStep}
                  </Button>
                )}
                
                {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={!validateStep(currentStep)}
                    className="flex-1 bg-[#1A3A4A] text-white hover:bg-[#1A3A4A]/90 rounded-full h-12 disabled:opacity-50"
                    data-testid="btn-next"
                  >
                    {content.nextStep}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.consent}
                    className="flex-1 bg-[#60B5AF] text-white hover:bg-[#60B5AF]/90 rounded-full h-12 disabled:opacity-50"
                    data-testid="btn-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {content.submitting}
                      </>
                    ) : (
                      <>
                        {content.submit}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactModal;
